import React, { useState, useEffect } from "react";
import { useUser } from "../../../context/UserContext";
import { toast } from "sonner";
import { 
  getVAPermissions, 
  updateVAPermissions,
  initializeVAPermissions,
  type VAPermissions 
} from "../../../service/apiService";
import { Globe, Users, Building2, AlertCircle, CheckCircle, Loader2,Cpu, Bot, Database, PlusCircle } from "lucide-react";

// Creation toggles. Unset on the server means allowed, hence the `!== false` reads below.
const CREATION_PERMISSIONS = [
  {
    key: "create_va",
    label: "Virtual Assistant Creation",
    description: "Allow regular users to create new Virtual Assistants",
    icon: Bot,
    iconClass: "text-violet-600",
  },
  {
    key: "create_brainbank",
    label: "BrainBank Creation",
    description: "Allow regular users to create new BrainBanks",
    icon: Database,
    iconClass: "text-teal-600",
  },
] as const;

const ApplicationSettings: React.FC = () => {
  const { showEmailAddresses, setShowEmailAddresses, isLoadingEmailSetting } = useUser();
  const [isUpdating, setIsUpdating] = useState(false);

  const [vaPermissions, setVAPermissions] = useState<VAPermissions>({
    share_with_everyone: false,
    assign_to_users: false,
    assign_to_department: false,
    change_model: false,
    create_va: true,
    create_brainbank: true
  });
  const [loadingVAPermissions, setLoadingVAPermissions] = useState(true);
  const [updatingVAPermissions, setUpdatingVAPermissions] = useState(false);

  // Load VA permissions on component mount
  useEffect(() => {
    const loadVAPermissions = async () => {
      try {
        setLoadingVAPermissions(true);
        const result = await getVAPermissions();
        
        if (result.success) {
          setVAPermissions(result.permissions);
        }
      } catch (error) {
        console.error("Error loading VA permissions:", error);
        // Try to initialize if they don't exist
        try {
          await initializeVAPermissions();
          const result = await getVAPermissions();
          if (result.success) {
            setVAPermissions(result.permissions);
          }
        } catch (initError) {
          console.error("Error initializing VA permissions:", initError);
          toast.error("Failed to load VA permissions");
        }
      } finally {
        setLoadingVAPermissions(false);
      }
    };

    loadVAPermissions();
  }, []);

  const handleToggleEmailVisibility = async () => {
    setIsUpdating(true);
    try {
      const newValue = !showEmailAddresses;
      await setShowEmailAddresses(newValue);
      
      if (newValue) {
        toast.success("Regular users can now browse email addresses");
      } else {
        toast.success("Email addresses are now hidden from regular users");
      }
    } catch (error) {
      console.error("Error updating email visibility setting:", error);
      toast.error("Failed to update email visibility setting");
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle VA permission changes
  const handleVAPermissionChange = async (permission: keyof VAPermissions, enabled: boolean) => {
    setUpdatingVAPermissions(true);
    try {
      const updatedPermissions = {
        ...vaPermissions,
        [permission]: enabled
      };

      const result = await updateVAPermissions(updatedPermissions);
      
      if (result.success) {
        setVAPermissions(result.permissions);
        
        const permissionLabels = {
          share_with_everyone: "Share VA with Everyone",
          assign_to_users: "Assign VA to Other Users", 
          assign_to_department: "Assign VA to Department",
          change_model: "Change VA Model",
          create_va: "Virtual Assistant Creation",
          create_brainbank: "BrainBank Creation"
        };
        
        const label = permissionLabels[permission];
        toast.success(`${label} ${enabled ? 'enabled' : 'disabled'} successfully`);
      } else {
        throw new Error('Failed to update VA permissions');
      }
    } catch (error) {
      console.error("Error updating VA permission:", error);
      toast.error("Failed to update VA permissions");
    } finally {
      setUpdatingVAPermissions(false);
    }
  };

  // Loading component with spinner
  const VAPermissionsLoading = () => (
    <div className="flex flex-col items-center justify-center py-12 space-y-4">
      <Loader2 className="w-8 h-8 text-violet-600 animate-spin" />
      <div className="text-center">
        <p className="text-sm font-medium text-gray-700">Loading Virtual Assistant Settings...</p>
        <p className="text-xs text-gray-500 mt-1">Please wait while we fetch your permissions</p>
      </div>
    </div>
  );

  // Alternative: Combined spinner with skeleton
  const VAPermissionsLoadingWithSkeleton = () => (
    <div className="space-y-6">
      {/* Loading header with spinner */}
      <div className="flex items-center justify-center py-4 border-b border-gray-100">
        <div className="flex items-center space-x-3">
          <Loader2 className="w-5 h-5 text-violet-600 animate-spin" />
          <span className="text-sm text-gray-600">Loading permissions...</span>
        </div>
      </div>
      
      {/* Skeleton placeholders */}
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0 opacity-50">
          <div className="flex items-center space-x-3">
            <div className="w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
            <div className="space-y-2">
              <div className="w-40 h-4 bg-gray-200 rounded animate-pulse"></div>
              <div className="w-56 h-3 bg-gray-200 rounded animate-pulse"></div>
              <div className="w-24 h-3 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
          <div className="w-11 h-6 bg-gray-200 rounded-full animate-pulse"></div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="p-6">
      <div className="space-y-8">
        <div className="bg-surface-overlay border border-border-default rounded-lg p-6">
          <h3 className="text-md font-semibold text-gray-900 mb-4">Email Visibility for VA Sharing</h3>
          
          <div className="flex items-center justify-between py-4">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900">Email Address Visibility for Regular Users</h4>
              <p className="text-xs text-gray-500 mt-1">
                Control whether regular users can browse email addresses. Admins always have full access.
              </p>
              <p className="text-xs mt-2 font-medium">
                {isLoadingEmailSetting ? (
                  <span className="text-gray-400 flex items-center space-x-2">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span>Loading setting...</span>
                  </span>
                ) : (
                  <>
                    Current status: <span className={showEmailAddresses ? "text-green-600" : "text-red-600"}>
                      {showEmailAddresses ? "Regular users CAN see emails" : "Regular users CANNOT see emails"}
                    </span>
                  </>
                )}
              </p>
            </div>
            <button
              onClick={handleToggleEmailVisibility}
              disabled={isUpdating || isLoadingEmailSetting}
              type="button"
              role="switch"
              aria-checked={showEmailAddresses}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${
                showEmailAddresses ? "bg-violet-600" : "bg-gray-200"
              } ${(isUpdating || isLoadingEmailSetting) ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <span
                aria-hidden="true"
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white dark:bg-zinc-200 shadow ring-0 transition duration-200 ease-in-out ${
                  showEmailAddresses ? "translate-x-5" : "translate-x-0"
                }`}
              />
              {/* Show spinner inside toggle when updating */}
              {isUpdating && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="w-3 h-3 text-gray-400 animate-spin" />
                </div>
              )}
            </button>
          </div>
        </div>

        {/* VA PERMISSIONS SECTION WITH SPINNER */}
        <div className="bg-surface-overlay border border-border-default rounded-lg p-6">
          <div className="flex items-center space-x-2 mb-4">
            <AlertCircle className="w-5 h-5 text-blue-600" />
            <h3 className="text-md font-semibold text-gray-900">
              Virtual Assistant Sharing Controls
            </h3>
            {/* Show spinner in header when updating */}
            {updatingVAPermissions && (
              <Loader2 className="w-4 h-4 text-violet-600 animate-spin ml-auto" />
            )}
          </div>
          
          <p className="text-sm text-gray-600 mb-6">
            Control Virtual Assistant permissions for regular users. Disabled options hide the corresponding sections. Admins always have full access.
          </p>

          {/* Show loading spinner while fetching VA permissions */}
          {loadingVAPermissions ? (
            // Choose one of these loading styles:
            // Option 1: Simple spinner with text
            <VAPermissionsLoading />
            
            // Option 2: Spinner with skeleton (uncomment to use)
            // <VAPermissionsLoadingWithSkeleton />
          ) : (
            <div className="space-y-6">
              
              {/* Share with Everyone Permission */}
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <Globe className="w-5 h-5 text-blue-600" />
                  <div>
                    <div className="font-medium text-gray-900">Share VA with Everyone</div>
                    <div className="text-sm text-gray-600">
                      Allow users to share their VAs with other individual users
                    </div>
                    <div className="text-xs mt-1 font-medium">
                      Status: <span className={vaPermissions.share_with_everyone ? "text-green-600" : "text-red-600"}>
                        {vaPermissions.share_with_everyone ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <button
                    onClick={() => handleVAPermissionChange('share_with_everyone', !vaPermissions.share_with_everyone)}
                    disabled={updatingVAPermissions}
                    type="button"
                    role="switch"
                    aria-checked={vaPermissions.share_with_everyone}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${
                      vaPermissions.share_with_everyone ? "bg-violet-600" : "bg-gray-200"
                    } ${updatingVAPermissions ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white dark:bg-zinc-200 shadow ring-0 transition duration-200 ease-in-out ${
                        vaPermissions.share_with_everyone ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                  {/* Individual spinner for this toggle */}
                  {updatingVAPermissions && (
                    <div className="absolute -right-8 top-0 h-6 flex items-center">
                      <Loader2 className="w-4 h-4 text-violet-600 animate-spin" />
                    </div>
                  )}
                </div>
              </div>

              {/* Assign to Other Users Permission */}
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <Users className="w-5 h-5 text-green-600" />
                  <div>
                    <div className="font-medium text-gray-900">Assign to Other Users</div>
                    <div className="text-sm text-gray-600">
                      Allow users to assign other users as managers of their VAs
                    </div>
                    <div className="text-xs mt-1 font-medium">
                      Status: <span className={vaPermissions.assign_to_users ? "text-green-600" : "text-red-600"}>
                        {vaPermissions.assign_to_users ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <button
                    onClick={() => handleVAPermissionChange('assign_to_users', !vaPermissions.assign_to_users)}
                    disabled={updatingVAPermissions}
                    type="button"
                    role="switch"
                    aria-checked={vaPermissions.assign_to_users}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${
                      vaPermissions.assign_to_users ? "bg-violet-600" : "bg-gray-200"
                    } ${updatingVAPermissions ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white dark:bg-zinc-200 shadow ring-0 transition duration-200 ease-in-out ${
                        vaPermissions.assign_to_users ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Assign to Department Permission */}
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center space-x-3">
                  <Building2 className="w-5 h-5 text-purple-600" />
                  <div>
                    <div className="font-medium text-gray-900">Assign to Department</div>
                    <div className="text-sm text-gray-600">
                      Allow users to assign their VAs to departments
                    </div>
                    <div className="text-xs mt-1 font-medium">
                      Status: <span className={vaPermissions.assign_to_department ? "text-green-600" : "text-red-600"}>
                        {vaPermissions.assign_to_department ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <button
                    onClick={() => handleVAPermissionChange('assign_to_department', !vaPermissions.assign_to_department)}
                    disabled={updatingVAPermissions}
                    type="button"
                    role="switch"
                    aria-checked={vaPermissions.assign_to_department}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${
                      vaPermissions.assign_to_department ? "bg-violet-600" : "bg-gray-200"
                    } ${updatingVAPermissions ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white dark:bg-zinc-200 shadow ring-0 transition duration-200 ease-in-out ${
                        vaPermissions.assign_to_department ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              </div>
              {/* Change VA Model Permission */}
              <div className="flex items-center justify-between py-3 border-t border-gray-100pt-6">
                <div className="flex items-center space-x-3">
                  <Cpu className="w-5 h-5 text-orange-600" />
                  <div>
                    <div className="font-medium text-gray-900">Change VA Model</div>
                    <div className="text-sm text-gray-600">
                      Allow users to change the default model assigned to their VAs
                    </div>
                    <div className="text-xs mt-1 font-medium">
                      Status: <span className={vaPermissions.change_model ? "text-green-600" : "text-red-600"}>
                        {vaPermissions.change_model ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <button
                    onClick={() => handleVAPermissionChange('change_model', !vaPermissions.change_model)}
                    disabled={updatingVAPermissions}
                    type="button"
                    role="switch"
                    aria-checked={vaPermissions.change_model}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${
                      vaPermissions.change_model ? "bg-violet-600" : "bg-gray-200"
                    } ${updatingVAPermissions ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white dark:bg-zinc-200 shadow ring-0 transition duration-200 ease-in-out ${
                        vaPermissions.change_model ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* CREATION CONTROLS */}
        <div className="bg-surface-overlay border border-border-default rounded-lg p-6">
          <div className="flex items-center space-x-2 mb-4">
            <PlusCircle className="w-5 h-5 text-violet-600" />
            <h3 className="text-md font-semibold text-gray-900">Creation Controls</h3>
            {updatingVAPermissions && (
              <Loader2 className="w-4 h-4 text-violet-600 animate-spin ml-auto" />
            )}
          </div>

          <p className="text-sm text-gray-600 mb-6">
            Choose whether regular users can create new items. When turned off, the Create
            buttons are hidden for them. Items they already have are not affected. Admins can always create.
          </p>

          {loadingVAPermissions ? (
            <VAPermissionsLoading />
          ) : (
            <div className="divide-y divide-gray-100">
              {CREATION_PERMISSIONS.map(({ key, label, description, icon: Icon, iconClass }) => {
                const enabled = vaPermissions[key] !== false;
                return (
                  <div key={key} className="flex items-center justify-between py-3">
                    <div className="flex items-center space-x-3">
                      <Icon className={`w-5 h-5 ${iconClass}`} />
                      <div>
                        <div id={`${key}-label`} className="font-medium text-gray-900">{label}</div>
                        <div className="text-sm text-gray-600">{description}</div>
                        <div className="text-xs mt-1 font-medium">
                          Status: <span className={enabled ? "text-green-600" : "text-red-600"}>
                            {enabled ? "Enabled" : "Disabled"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleVAPermissionChange(key, !enabled)}
                      disabled={updatingVAPermissions}
                      type="button"
                      role="switch"
                      aria-checked={enabled}
                      aria-labelledby={`${key}-label`}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${
                        enabled ? "bg-violet-600" : "bg-gray-200"
                      } ${updatingVAPermissions ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white dark:bg-zinc-200 shadow ring-0 transition duration-200 ease-in-out ${
                          enabled ? "translate-x-5" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationSettings;